import React from "react";
import { connect } from "react-redux";

import Dialog from "../core/components/dialog/Dialog";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogOK from "../core/components/dialog/DialogOK";
import File from "../core/types/files/File";
import FileExplorer from "../core/components/fileExplorer/FileExplorer";
import Grid from "../core/components/grid/Grid";
import GridColumn, { GridColumnType } from "../core/components/grid/GridColumn";
import Import from "../core/components/import/Import";
import Info from "../core/components/info/Info";
import InputForm from "../core/components/inputform/InputForm";
import InputFormField from "../core/components/inputform/InputFormField";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import Panel from "../core/components/panels/Panel";
import SelectList from "../core/components/inputform/SelectList";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import Wizard from "../core/components/wizard/Wizard";
import WizardStep from "../core/components/wizard/WizardStep";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { AppDispatch, IAppState } from "../core/state/Store";
import { DataSet } from "../core/types/data/DataSet";
import { DataTable } from "../core/types/data/DataTable";
import { FileExplorerTypes } from "../core/types/system/FileExplorer";
import {
    ImportLog,
    SkippedRecordCount,
    UpsertCount,
} from "../core/types/core/ImportLog";
import { Alignment, Side } from "../core/types/system/Layout";
import {
    SpreadsheetConfiguration,
    SpreadsheetTableDefinition,
} from "../core/types/data/SpreadsheetConfiguration";
import Subject from "../core/types/core/Subject";
import Faculty from "../core/types/core/Faculty";
import YearLevel from "../core/types/core/YearLevel";
import Toggle from "../core/components/inputform/Toggle";
import Text from "../core/components/text/Text";
import { addAlert } from "../core/state/system/Alerts";
import {
    addSubject,
    archiveSubject,
    importSubjectsFromTD,
    initialiseSubjectDetails,
    updateSubject,
} from "../core/state/core/Subjects";
import { initialiseFacultyDetails } from "../core/state/core/Faculties";
import { initialiseYearLevelDetails } from "../core/state/core/YearLevel";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import RenderOrientations from "../core/types/enums/RenderOrientation";
import InputFormMandatory from "../core/components/inputform/InputFormMandatory";
import SortDialog from "../core/components/sorting/SortDialog";
import { AcademicPeriods } from "../core/types/enums/AcademicPeriods";
import { SubjectGenders } from "../core/types/enums/SubjectGenders";
import InputFieldTypes from "../core/types/enums/ImportFieldTypes";
import Tabs from "../core/components/tabs/Tabs";
import TabContainer from "../core/components/tabs/TabContainer";
import RichText from "../core/components/inputform/RichText";
import Utility from "../core/foundation/Utility";

import template from "../resources/Subject_Import_Template.xlsx";

import "./Subjects.scss";

interface RouteSubjectsProps {
    archives: Subject[];
    subjects: Subject[];
    faculties: Faculty[];
    yearLevels: YearLevel[];

    setPageLoading: (loading: boolean) => void;
    initialiseSubjectDetails: () => Promise<void>;
    initialiseFacultyDetails: () => Promise<void>;
    initialiseYearLevelDetails: () => Promise<void>;
    addSubject: (details: Subject[]) => Promise<void>;
    updateSubject: (details: Subject[]) => Promise<void>;
    archiveSubject: (details: Subject[]) => Promise<void>;
}

interface RouteSubjectsState {
    archives: boolean;

    editOpen: boolean;
    editLoad: boolean;
    editTitle: string;
    editStep: number;

    sorting: Subject[];
    sortOpen: boolean;
    sortLoad: boolean;

    importOpen: boolean;
    importLoad: boolean;
    importType: string;
    importStep: number;
    importWidth: number;
    importValid: boolean;

    selection: Subject | null;
    selections: Subject[];

    editing: Subject | null;
}

class RouteSubjects extends React.Component<
    RouteSubjectsProps,
    RouteSubjectsState
> {
    state: RouteSubjectsState = {
        archives: false,

        editOpen: false,
        editLoad: false,
        editTitle: "",
        editStep: 0,

        sorting: [],
        sortOpen: false,
        sortLoad: false,

        importOpen: false,
        importLoad: false,
        importType: undefined,
        importStep: 0,
        importWidth: 400,
        importValid: false,

        selection: null,
        selections: [],

        editing: null,
    };

    static getDerivedStateFromProps(
        props: RouteSubjectsProps,
        state: RouteSubjectsState,
    ) {
        return {
            importWidth:
                state.importStep === 0
                    ? state.importType === "csv"
                        ? 1000
                        : state.importType === "tdf"
                        ? 580
                        : 580
                    : 580,
        };
    }

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);

            await Promise.all([
                this.props.initialiseSubjectDetails(),
                this.props.initialiseFacultyDetails(),
                this.props.initialiseYearLevelDetails(),
            ]);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    add() {
        this.setState({
            editing: new Subject({
                id: 0,
                code: "",
                bosCode: "",
                name: "",
                description: "",
                facultyID: undefined,
                noOfSubgrids: 1,
                load: 0,
                unitValue: 0,
                maxClassSize: 0,
                codeGenScheme: 1010008,
                gender: SubjectGenders.All,
                orderNo: this.props.subjects.map(s => s.orderNo).max() + 1,
            }),
            editOpen: true,
            editLoad: false,
            editTitle: "Add Subject",
            editStep: 0,
        });
    }

    async edit() {
        if (!this.state.selection) return;

        try {
            this.props.setPageLoading(true);

            this.setState({
                editing: new Subject(this.state.selection),
                editOpen: true,
                editLoad: false,
                editTitle: "Edit Subject",
                editStep: 0,
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    cancelEdit() {
        this.setState({ editing: null, editOpen: false });
    }

    async saveEdit() {
        if (!this.state.editing) return;

        try {
            this.setState({ editLoad: true });

            if (this.state.editing.id === 0)
                await this.props.addSubject([this.state.editing]);
            else await this.props.updateSubject([this.state.editing]);
        } finally {
            this.setState({ editing: null, editLoad: false, editOpen: false });
        }
    }

    async rowEdit(record: Subject, changes: Subject) {
        if (!this.state.archives) {
            this.props.updateSubject([new Subject({ ...record, ...changes })]);
        }
    }

    async delete() {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "delete_forever",
            styleColor: "red",
            title: "Archive Subjects",
            text: `Are you sure you want to archive the listed subjects?<br/><ul class="indented chevron"><li>${this.state.selections
                .map(item => item.display)
                .join("</li><li>")}</li></ul>`,
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.archiveSubject(this.state.selections);

                this.setState({ selection: null, selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    sort() {
        this.setState({
            sorting: this.props.subjects
                .map(subject => new Subject(subject))
                .sort(Utility.getSorter([`order`])),
            sortOpen: true,
        });
    }

    async saveSort(sorted: Subject[]) {
        try {
            this.setState({ sortLoad: true });

            for (const [newIndex, item] of sorted.entries())
                item.orderNo = newIndex;

            await this.props.updateSubject(sorted);

            this.setState({ sortOpen: false });
        } finally {
            this.setState({ sortLoad: false });
        }
    }

    import() {
        this.setState({
            importOpen: true,
            importStep: 0,
            importType: undefined,
        });
    }

    importSetType(type: string) {
        this.setState({ importType: type, importStep: 0 });
    }

    importSetStep(step: number) {
        this.setState({ importStep: step });
    }

    importNextStep() {
        this.setState({ importStep: this.state.importStep + 1 });
    }

    importLastStep() {
        this.setState({ importStep: this.state.importStep - 1 });
    }

    importProceedChanged(valid: boolean) {
        this.setState({ importValid: valid });
    }

    importCancel() {
        this.setState({ importOpen: false });
    }

    classesTooltip(subject: Subject): string {
        return subject.classes?.length > 0
            ? '<ul class="no-style"><li>' +
                  subject.classes
                      ?.map(
                          item =>
                              `${
                                  subject.noOfSubgrids > 1
                                      ? "Semester " + item.subgrid
                                      : "All Year"
                              } - ${item.code}`,
                      )
                      .join("</li><li>") +
                  "</li></ul>"
            : "No classes";
    }

    render() {
        return (
            <section>
                <MainToolbarContents>
                    <Toggle
                        onLabel="Archives"
                        offLabel="Archives"
                        value={this.state.archives}
                        onChange={value =>
                            this.setState({ archives: value ?? false })
                        }
                    />
                    <ToolbarButton
                        icon="add"
                        text="Add"
                        tooltip="Add a new year level"
                        onClick={this.add.bind(this)}
                    />
                    <ToolbarButton
                        icon="edit"
                        text="Edit"
                        tooltip={`Edit the selected year level${
                            this.state.selections.length > 1 ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.edit.bind(this)}
                    />
                    <ToolbarButton
                        icon="archive"
                        text="Archive"
                        tooltip="Archive the selected year levels"
                        disabled={this.state.selections.length === 0}
                        onClick={this.delete.bind(this)}
                    />
                    <ToolbarButton
                        icon="filter_list"
                        text="Sort"
                        tooltip="Sort all year levels"
                        onClick={this.sort.bind(this)}
                    />
                    <ToolbarButton
                        icon="system_update_alt"
                        text="Import"
                        tooltip="Import year levels"
                        onClick={this.import.bind(this)}
                    />
                </MainToolbarContents>

                {(this.state.archives
                    ? this.props.archives?.length
                    : this.props.subjects?.length) > 0 ? (
                    <Grid<Subject>
                        value={
                            this.state.archives
                                ? this.props.archives
                                : this.props.subjects
                        }
                        window={true}
                        collapsed={true}
                        keyField="id"
                        defaultSortField="orderNo"
                        selectionChanged={(selection, selections) =>
                            this.setState({ selection, selections })
                        }
                        cellDoubleClicked={this.edit.bind(this)}
                        valueEdited={this.rowEdit.bind(this)}>
                        <GridColumn
                            header="No"
                            field="orderNo"
                            selectable={true}
                            alignment={Alignment.Left}
                            width="40px"
                        />
                        <GridColumn
                            header="Faculty"
                            field="facultyID"
                            selectable={true}
                            editable={true}
                            filterable={true}
                            type={GridColumnType.Dropdown}
                            blank={true}
                            options={this.props.faculties.map(f => ({
                                value: f.id,
                                text: f.display,
                                order: f.order,
                            }))}
                        />
                        <GridColumn
                            header="Year Level"
                            field="yearLevel"
                            selectable={true}
                            editable={true}
                            filterable={true}
                            type={GridColumnType.Dropdown}
                            blank={true}
                            options={this.props.yearLevels.map(yl => ({
                                value: yl.code,
                                text: yl.display,
                                order: yl.order,
                            }))}
                        />
                        <GridColumn
                            header="Code"
                            field="code"
                            selectable={true}
                            editable={true}
                            filterable={true}
                        />
                        <GridColumn
                            header="Name"
                            field="name"
                            selectable={true}
                            editable={true}
                            filterable={true}
                        />
                        <GridColumn
                            header="Load"
                            field="load"
                            selectable={true}
                            editable={true}
                            filterable={true}
                            type={GridColumnType.Number}
                            decimalPlaces={2}
                        />
                        <GridColumn
                            header="Academic Period"
                            field="noOfSubgrids"
                            selectable={true}
                            editable={true}
                            filterable={true}
                            type={GridColumnType.Dropdown}
                            options={Utility.enumToOptions(
                                AcademicPeriods,
                                "number",
                            )}
                        />
                        <GridColumn
                            header="Gender"
                            field="gender"
                            selectable={true}
                            editable={true}
                            filterable={true}
                            type={GridColumnType.Dropdown}
                            options={Utility.enumToOptions(SubjectGenders)}
                        />
                        <GridColumn
                            header="Classes"
                            field="instances"
                            selectable={true}
                            editable={true}
                            filterable={true}
                            type={GridColumnType.Number}
                            decimalPlaces={0}
                            cellTooltipMethod={(item: Subject) =>
                                this.classesTooltip(item)
                            }
                            cellTooltipHtml={true}
                        />
                    </Grid>
                ) : (
                    <Text clearanceSide={Side.All}>
                        {this.state.archives
                            ? "No archived subjects found."
                            : "No active subjects found."}
                    </Text>
                )}

                <Dialog
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoad}
                    dialogIcon="edit"
                    dialogTitle={this.state.editTitle}
                    canResize={false}
                    width={900}>
                    <DialogContent>
                        <Tabs boxed={true}>
                            <TabContainer header="Details">
                                <Panel>
                                    <Panel>
                                        <InputForm
                                            binding={this.state.editing}
                                            display={
                                                RenderOrientations.Vertical
                                            }
                                            onChange={value =>
                                                this.setState({
                                                    editing: value,
                                                })
                                            }>
                                            <InputFormField
                                                label="Code"
                                                field="code"
                                                mandatory={true}
                                            />
                                        </InputForm>
                                        <InputForm
                                            binding={this.state.editing}
                                            display={
                                                RenderOrientations.Horizontal
                                            }
                                            onChange={value =>
                                                this.setState({
                                                    editing: value,
                                                })
                                            }>
                                            <InputFormField
                                                label="BOS Code"
                                                field="bosCode"
                                            />
                                            <InputFormField
                                                label="Gender"
                                                field="gender"
                                                mandatory={true}
                                                type={InputFieldTypes.Select}
                                                options={Utility.enumToOptions(
                                                    SubjectGenders,
                                                )}
                                            />
                                        </InputForm>
                                        <InputForm
                                            binding={this.state.editing}
                                            display={
                                                RenderOrientations.Vertical
                                            }
                                            onChange={value =>
                                                this.setState({
                                                    editing: value,
                                                })
                                            }>
                                            <InputFormField
                                                label="Name"
                                                field="name"
                                                mandatory={true}
                                            />
                                        </InputForm>
                                        <InputForm
                                            binding={this.state.editing}
                                            display={
                                                RenderOrientations.Horizontal
                                            }
                                            columns={2}
                                            onChange={value =>
                                                this.setState({
                                                    editing: value,
                                                })
                                            }>
                                            <InputFormField
                                                label="Year Level"
                                                field="yearLevel"
                                                type={InputFieldTypes.Select}
                                                blank={true}
                                                options={this.props.yearLevels.map(
                                                    yl => ({
                                                        value: yl.code,
                                                        text: yl.display,
                                                        order: yl.order,
                                                    }),
                                                )}
                                            />
                                            <InputFormField
                                                label="Faculty"
                                                field="facultyID"
                                                type={InputFieldTypes.Select}
                                                blank={true}
                                                options={this.props.faculties.map(
                                                    f => ({
                                                        value: f.id,
                                                        text: f.display,
                                                        order: f.order,
                                                    }),
                                                )}
                                            />
                                            <InputFormField
                                                label="Academic Period"
                                                field="noOfSubgrids"
                                                type={InputFieldTypes.Select}
                                                options={Utility.enumToOptions(
                                                    AcademicPeriods,
                                                    "number",
                                                )}
                                            />
                                            <InputFormField
                                                label="Max Class Size"
                                                field="maxClassSize"
                                                type={InputFieldTypes.Number}
                                                decimalPlaces={0}
                                            />
                                            <InputFormField
                                                label="Unit Value"
                                                field="unitValue"
                                                type={InputFieldTypes.Number}
                                                decimalPlaces={2}
                                            />
                                            <InputFormField
                                                label="Load"
                                                field="load"
                                                type={InputFieldTypes.Number}
                                                decimalPlaces={2}
                                            />
                                        </InputForm>
                                    </Panel>
                                </Panel>
                            </TabContainer>
                            <TabContainer header="Description">
                                <Panel>
                                    <RichText
                                        value={this.state.editing?.description}
                                        onChange={value =>
                                            this.setState({
                                                editing: new Subject({
                                                    ...this.state.editing,
                                                    description: value,
                                                }),
                                            })
                                        }
                                    />
                                </Panel>
                            </TabContainer>
                        </Tabs>

                        <InputFormMandatory />
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Cancel"
                            onClick={this.cancelEdit.bind(this)}
                        />
                        <DialogOK
                            text="Save"
                            onClick={this.saveEdit.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.importOpen}
                    showLoading={this.state.importLoad}
                    dialogIcon="system_update_alt"
                    dialogTitle="Import"
                    canResize={false}
                    width={this.state.importWidth}>
                    <DialogContent>
                        <ConnectedImportWizard
                            importType={this.state.importType}
                            importStep={this.state.importStep}
                            setType={this.importSetType.bind(this)}
                            setStep={this.importSetStep.bind(this)}
                            validChanged={this.importProceedChanged.bind(this)}
                            setLoading={value =>
                                this.setState({ importLoad: value })
                            }
                        />
                    </DialogContent>
                    <DialogFooter>
                        {this.state.importStep !== 1 && (
                            <DialogCancel
                                text={
                                    this.state.importStep < 1
                                        ? "Cancel"
                                        : "Close"
                                }
                                onClick={this.importCancel.bind(this)}
                            />
                        )}
                        {this.state.importStep < 1 && (
                            <DialogOK
                                text="Next"
                                disabled={!this.state.importValid}
                                onClick={this.importNextStep.bind(this)}
                            />
                        )}
                    </DialogFooter>
                </Dialog>

                <SortDialog<Subject>
                    keyField="id"
                    value={this.state.sorting}
                    showDialog={this.state.sortOpen}
                    showLoading={this.state.sortLoad}
                    onSave={this.saveSort.bind(this)}
                    onCancel={() => this.setState({ sortOpen: false })}>
                        <GridColumn
                            header="No"
                            field="orderNo"
                            alignment={Alignment.Left}
                            width="40px"
                        />
                        <GridColumn
                            header="Faculty"
                            field="facultyID"
                            type={GridColumnType.Dropdown}
                            options={this.props.faculties.map(f => ({
                                value: f.id,
                                text: f.display,
                                order: f.order,
                            }))}
                        />
                        <GridColumn
                            header="Year Level"
                            field="yearLevel"
                            type={GridColumnType.Dropdown}
                            options={this.props.yearLevels.map(yl => ({
                                value: yl.code,
                                text: yl.display,
                                order: yl.order,
                            }))}
                        />
                        <GridColumn
                            header="Code"
                            field="code"
                        />
                        <GridColumn
                            header="Name"
                            field="name"
                        />
                        <GridColumn
                            header="Load"
                            field="load"
                            type={GridColumnType.Number}
                            decimalPlaces={2}
                        />
                        <GridColumn
                            header="Academic Period"
                            field="noOfSubgrids"
                            type={GridColumnType.Dropdown}
                            options={Utility.enumToOptions(
                                AcademicPeriods,
                                "number",
                            )}
                        />
                        <GridColumn
                            header="Gender"
                            field="gender"
                            type={GridColumnType.Dropdown}
                            options={Utility.enumToOptions(SubjectGenders)}
                        />
                        <GridColumn
                            header="Classes"
                            field="instances"
                            type={GridColumnType.Number}
                            decimalPlaces={0}
                        />
                </SortDialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { subject, faculty, yearLevel } = state.db;
    return {
        archives: subject.archives,
        subjects: subject.subjects,
        faculties: faculty.flat,
        yearLevels: yearLevel.yearLevels,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseSubjectDetails: () =>
            unwrappedDispatch(initialiseSubjectDetails()),
        initialiseFacultyDetails: () =>
            unwrappedDispatch(initialiseFacultyDetails()),
        initialiseYearLevelDetails: () =>
            unwrappedDispatch(initialiseYearLevelDetails()),
        addSubject: (details: Subject[]) =>
            unwrappedDispatch(addSubject(details)),
        updateSubject: (details: Subject[]) =>
            unwrappedDispatch(updateSubject(details)),
        archiveSubject: (details: Subject[]) =>
            unwrappedDispatch(archiveSubject(details)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteSubjects);

interface ImportWizardProps {
    subjects: Subject[];
    importType?: string;
    importStep: number;

    addSubject: (details: Subject[]) => Promise<void>;
    updateSubject: (details: Subject[]) => Promise<void>;
    setType?: (type: string) => void;
    setStep?: (step: number) => void;
    setLoading?: (loading: boolean) => void;
    validChanged?: (valid: boolean) => void;
}

interface ImportWizardState {
    importData?: DataSet;
    importFile?: File;
    importLogs?: ImportLog;
}

class ImportWizard extends React.Component<
    ImportWizardProps,
    ImportWizardState
> {
    state: ImportWizardState = {};

    private importTypes = [
        { value: "csv", text: "XLSX/CSV", order: 1 },
        { value: "tdf", text: "TD File", order: 2 },
    ];

    private importConfig: SpreadsheetConfiguration = {
        Name: "Subjects",
        Tables: [
            {
                Name: "Subjects",
                Columns: [
                    { PK: true, Field: "code", Name: "Code" },
                    { Field: "bosCode", Name: "BOS Code" },
                    { Field: "name", Name: "Name" },
                    { Field: "description", Name: "Description" },
                    {
                        Field: "noOfSubgrids",
                        Name: "No Of Semesters",
                    },
                    { Field: "unitValue", Name: "Unit Value" },
                    { Field: "subjectValue", Name: "Subject Value" },
                    { Field: "load", Name: "Load" },
                    { Field: "maxClassSize", Name: "Max Class Size" },
                    { Field: "gender", Name: "Gender" },
                    { Field: "yearLevel", Name: "Year Level" },
                ],
            },
        ],
    };

    componentDidMount(): void {
        this.setState({
            importData: undefined,
        });
    }

    componentDidUpdate(
        prevProps: Readonly<ImportWizardProps>,
        prevState: Readonly<ImportWizardState>,
        snapshot?: any,
    ): void {
        if (prevProps.importType !== this.props.importType) {
            this.setState({
                importData: undefined,
                importFile: undefined,
            });

            this.props.validChanged?.(false);
        }

        if (prevProps.importStep !== this.props.importStep) {
            this.props.validChanged?.(this.importCanProceed());

            this.importProceed();
        } else if (prevState !== this.state) {
            this.props.validChanged?.(this.importCanProceed());
        }
    }

    importCanProceed(): boolean {
        switch (this.props.importStep) {
            case 0:
                switch (this.props.importType) {
                    case "csv":
                        return this.state.importData !== undefined;
                    case "tdf":
                        return this.state.importFile !== undefined;
                    default:
                        return false;
                }
            default:
                return false;
        }
    }

    importProceed() {
        switch (this.props.importStep) {
            case 1:
                switch (this.props.importType) {
                    case "csv":
                        this.doCsvImport();
                        break;
                    case "tdf":
                        this.doTDImport();
                        break;
                }
                break;
        }
    }

    async doCsvImport() {
        try {
            this.props.setStep?.(1);

            const adds = [];
            const upd = [];
            const logs: ImportLog = {
                upsertCounts: [],
                skippedItems: [],
            };

            for (const config of this.importConfig.Tables) {
                const table = this.state.importData[config.Name];
                if (!table) continue;

                this.processCsvImport(config, table, adds, upd, logs);
            }

            const waits: Promise<void>[] = [];
            if (adds.length > 0) waits.push(this.props.addSubject(adds));
            if (upd.length > 0) waits.push(this.props.updateSubject(upd));
            await Promise.all(waits);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    processCsvImport(
        type: SpreadsheetTableDefinition,
        data: DataTable,
        adds: Subject[],
        upd: Subject[],
        logs: ImportLog,
    ): void {
        for (const row of data) {
            const pk = type.Columns.find(c => c.PK)?.Field;
            if (!pk) continue;

            const pkVal = row[pk]?.toString().trim();
            if (!pkVal) continue;

            let subject = this.props.subjects?.find(
                u => u[pk]?.toLowerCase() === pkVal.toLowerCase(),
            );

            if (!subject) {
                subject = new Subject({
                    [pk]: pkVal,
                });

                adds.push(subject);
            } else {
                if (!upd.includes(subject)) upd.push(subject);
            }

            for (const column of type.Columns) {
                if (column.PK) continue;

                const value = row[column.Field]?.toString().trim();
                if (!value) continue;

                switch (column.Field) {
                    default:
                        Reflect.set(
                            subject,
                            column.Field as keyof Subject,
                            value,
                        );
                        break;
                }
            }
        }

        logs.upsertCounts.push({
            name: type.Name,
            insertCount: adds.length,
            updateCount: upd.length,
            archiveCount: 0,
        });
    }

    async doTDImport() {
        try {
            this.props.setStep?.(1);

            const logs = await importSubjectsFromTD(this.state.importFile.id);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    render() {
        return (
            <>
                <SelectList
                    tabs={true}
                    value={this.props.importType}
                    options={this.importTypes}
                    onChange={value =>
                        this.props.importStep === 0 &&
                        this.props.setType?.(value)
                    }
                />

                <Wizard
                    flow={true}
                    frameless={true}
                    transparent={true}
                    clearanceSide={Side.Top}
                    step={this.props.importStep}>
                    <WizardStep step={0}>
                        {(() => {
                            switch (this.props.importType) {
                                case "csv":
                                    return (
                                        <Import
                                            config={this.importConfig}
                                            template={template}
                                            templateName={template.stripReactName()}
                                            onReady={(_, data) =>
                                                this.setState({
                                                    importData: data,
                                                })
                                            }
                                        />
                                    );
                                case "tdf":
                                    return (
                                        <FileExplorer
                                            type={FileExplorerTypes.Timetables}
                                            value={this.state.importFile}
                                            onChange={item =>
                                                this.setState({
                                                    importFile: item,
                                                })
                                            }
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </WizardStep>
                    <WizardStep
                        step={1}
                        framed={true}
                        hiddenWhenOffscreen={true}>
                        <div className="buffer text-center">
                            <h2>We're currently importing your data</h2>
                        </div>
                        <div className="process-indicator">
                            <div className="cube cube1"></div>
                            <div className="cube cube2"></div>
                            <div className="cube cube3"></div>
                            <div className="cube cube4"></div>
                            <div className="cube cube5"></div>
                            <div className="cube cube6"></div>
                            <div className="cube cube7"></div>
                            <div className="cube cube8"></div>
                            <div className="cube cube9"></div>
                            <div className="cube cube10"></div>
                            <div className="cube cube11"></div>
                            <div className="document1"></div>
                            <div className="document2"></div>
                        </div>
                    </WizardStep>
                    <WizardStep
                        step={2}
                        hiddenWhenOffscreen={true}>
                        <Panel>
                            <Panel
                                title="Addition/Update Counts"
                                bufferSide={Side.All}>
                                {this.state?.importLogs?.upsertCounts?.length >
                                0 ? (
                                    <Grid<UpsertCount>
                                        value={
                                            this.state.importLogs
                                                ?.upsertCounts ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Added"
                                            field="insertCount"
                                        />
                                        <GridColumn
                                            header="Updated"
                                            field="updateCount"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>
                                        No records were added or updated.
                                    </Info>
                                )}
                            </Panel>

                            <Panel
                                title="Skipped Items"
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}>
                                {this.state?.importLogs?.skippedItems?.length >
                                0 ? (
                                    <Grid<SkippedRecordCount>
                                        value={
                                            this.state.importLogs
                                                ?.skippedItems ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Number"
                                            field="count"
                                        />
                                        <GridColumn
                                            header="Reason"
                                            field="reason"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>No records were skipped.</Info>
                                )}
                            </Panel>
                        </Panel>
                    </WizardStep>
                </Wizard>
            </>
        );
    }
}

const mapStateToPropsWizard = (state: IAppState) => {
    const { subject } = state.db;
    return {
        subjects: subject.subjects,
    };
};

const mapDispatchToPropsWizard = (_dispatch: AppDispatch) => {
    return {
        addSubject: (details: Subject[]) =>
            unwrappedDispatch(addSubject(details)),
        updateSubject: (details: Subject[]) =>
            unwrappedDispatch(updateSubject(details)),
    };
};

const ConnectedImportWizard = connect(
    mapStateToPropsWizard,
    mapDispatchToPropsWizard,
)(ImportWizard);
